import React from 'react';
import { Box, Typography } from '@mui/material';
import { DataTable, DownloadButton, Modal, formatDateRange, type DataTableColumn } from '../../../../ui-kit';
import type { CampaignBreakdownLine, CampaignBreakdownPayload } from '../../../../types/plannerDashboard';
import { percent, whole } from '../plannerFormat';

interface CampaignFunnelDialogProps {
  /** The campaign clicked, named at once while its hours load. */
  target: { channel: string; campaign: string } | null;
  payload: CampaignBreakdownPayload | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onDownload: () => void;
  downloading?: boolean;
  problem?: string | null;
}

type Line = CampaignBreakdownLine & { key: string; label: string; total?: boolean };

/**
 * One campaign from the heatmap, hour by hour: how many leads arrived in each
 * hour, and the share that were contacted, applied and went in force — side
 * by side, so an hour is judged on volume and conversion together (Thomas,
 * Amplify marketing, 21 Oct 2026: which hours is this campaign worth paying
 * for?).
 *
 * A window rather than a row opened in place, as the Scorecard and Daily Call
 * Plan do it: the grid runs 24 hours wide and scrolls sideways, and a table of
 * four rates under one of its rows would sit inside that scroll.
 */
export default function CampaignFunnelDialog({
  target, payload, loading, error, onClose, onDownload, downloading, problem,
}: CampaignFunnelDialogProps) {
  // The payload for a campaign other than the one now clicked is on its way out.
  const shown = payload && target && payload.campaign === target.campaign && payload.channel === target.channel
    ? payload : null;
  const base = shown?.base_stage.label ?? 'MQLs';
  const clock = shown?.hours?.label;
  const unit = shown?.unit ?? 'hour';

  const lines: Line[] = shown
    ? [
        ...shown.rows.map((r) => ({ ...r, label: r.short_name })),
        { ...shown.total, key: 'all', label: `All ${unit}s`, total: true },
      ]
    : [];

  const strong = (row: Line, node: React.ReactNode) =>
    row.total ? <Box component="span" sx={{ fontWeight: 700 }}>{node}</Box> : node;

  const columns: DataTableColumn<Line>[] = [
    {
      key: 'hour',
      header: clock ? `${unit.charAt(0).toUpperCase()}${unit.slice(1)} (${clock})` : unit.charAt(0).toUpperCase() + unit.slice(1),
      sticky: true,
      width: 120,
      render: (r) => strong(r, r.label),
    },
    {
      key: 'base',
      header: base,
      numeric: true,
      render: (r) => (
        <Box component="span" sx={(t) => ({ color: r.base === 0 ? t.tokens.text.tertiary : undefined })}>
          {strong(r, whole(r.base))}
        </Box>
      ),
    },
    ...(shown?.stages ?? []).map((st, i) => ({
      key: st.key,
      header: `${base} to ${st.label}`,
      numeric: true,
      render: (r: Line) => {
        const step = r.steps[i];
        if (!step || r.base === 0) {
          return <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Box>;
        }
        // The count beside the rate, so 8.5% reads as the four applications
        // it is; an hour too thin to rate keeps its count.
        return (
          <Box component="span" sx={(t) => ({ color: step.rate === null ? t.tokens.text.tertiary : undefined })}>
            {strong(r, step.rate === null ? '—' : percent(step.rate))}
            <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary, ml: 0.5 })}>({whole(step.hits)})</Box>
          </Box>
        );
      },
    })),
  ];

  const subtitle = shown
    ? [
        shown.channel,
        formatDateRange(shown.range),
        clock ? `hour each lead arrived, in ${clock}` : null,
      ].filter(Boolean).join(' · ')
    : target?.channel;

  return (
    <Modal
      open={!!target}
      onClose={onClose}
      size="lg"
      title={target?.campaign ?? ''}
      subtitle={subtitle}
      loading={loading && !shown}
      loadingHeight={520}
      actions={
        <DownloadButton
          formats={[{
            key: 'xlsx',
            label: 'Excel (.xlsx)',
            description: `This campaign by ${unit}, with the counts behind each rate`,
            onDownload,
          }]}
          tooltip="Download this campaign"
          loading={downloading}
          disabled={!shown}
        />
      }
    >
      {error ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{error}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            Of the {base} arriving in each {unit}, the share that reached each later step. The number in
            brackets is how many did.
          </Typography>
          <DataTable
            ariaLabel={shown?.title ?? 'Campaign by hour of day'}
            columns={columns}
            groups={[{ key: 'rows', title: '', rows: lines }]}
            getRowKey={(r) => r.key}
            collapsibleGroups={false}
            columnPicker={false}
            emptyMessage="No leads for this campaign in this range."
          />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {shown && shown.min_leads > 0
              ? `${unit.charAt(0).toUpperCase()}${unit.slice(1)}s with fewer than ${shown.min_leads} ${base} show a count but no rate: too few to tell.`
              : `Every ${unit} with any ${base} is rated, however few.`}
          </Typography>
          {problem && (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
          )}
        </Box>
      )}
    </Modal>
  );
}
